/**
 * HospitalSelfRegisterPage
 * ------------------------
 * Public, comprehensive self-registration form for brand-new hospitals that
 * have never ordered through Sterile Mate. Posts to
 *   POST /api/hospitals/register/request
 * and lands the request on the admin Pending Approvals widget. Once an admin
 * approves, the system emails an access code and the contact can log in at
 * /hospital-login.
 *
 * Sections (single page, grouped):
 *   1. Facility profile (name, type, NPI, beds, address)
 *   2. Primary user (name, title, department, email, phone)
 *   3. Payment terms preference (Net 30 / Prepay)
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  User,
  MapPin,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Mail,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FACILITY_TYPES = [
  "Hospital",
  "Surgery Center",
  "Dialysis Center",
  "Outpatient Clinic",
  "Long-Term Care",
  "Specialty Practice",
  "Other",
];

const DEPARTMENTS = [
  "Supply Chain / Materials Management",
  "Sterile Processing",
  "Operating Room",
  "Biomedical Engineering",
  "Facilities / EVS",
  "Dialysis",
  "Pharmacy",
  "Laboratory",
  "Nursing Administration",
  "Administration / Finance",
  "Other",
];

export default function HospitalSelfRegisterPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    // Facility
    hospital_name: "",
    facility_type: "Hospital",
    npi: "",
    bed_count: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    // Primary user
    contact_name: "",
    title: "",
    department: "Supply Chain / Materials Management",
    email: "",
    phone: "",
    // Terms
    payment_terms_preference: "Net 30",
    reason: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hospital_name.trim()) return toast.error("Facility name is required");
    if (!form.contact_name.trim()) return toast.error("Your name is required");
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Please enter a valid work email");
    if (!form.phone.trim()) return toast.error("A phone number helps us reach you for credit review");

    setSubmitting(true);
    try {
      await axios.post(`${API}/hospitals/register/request`, {
        ...form,
        bed_count: form.bed_count ? Number(form.bed_count) : null,
        source: "self_signup",
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Registration failed — please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-16 text-slate-100"
        style={{
          backgroundImage:
            "radial-gradient(1100px 600px at 10% 10%, rgba(14,165,233,0.25), transparent 60%)," +
            "radial-gradient(900px 600px at 90% 80%, rgba(37,99,235,0.20), transparent 55%)," +
            "linear-gradient(180deg, #0a1733 0%, #0b2148 50%, #0a1f44 100%)",
        }}
        data-testid="hospital-register-success"
      >
        <div className="max-w-md w-full bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-300" />
          </div>
          <h1 className="text-2xl font-extrabold text-white mb-2">Thanks — we&apos;re reviewing your registration</h1>
          <p className="text-sm text-slate-300 leading-relaxed mb-6">
            Our team will review your account within <strong className="text-white">one business day</strong>.
            {form.payment_terms_preference === "Net 30" && " Net 30 terms require a brief credit review."}
            {" "}You&apos;ll receive a welcome email at <strong className="text-amber-200">{form.email}</strong> with your login details as soon as we approve.
          </p>
          <p className="text-xs text-slate-400 mb-6">
            Need to place an urgent order in the meantime? Email
            {" "}<a href="mailto:orders@sterilemate.com" className="text-amber-300 hover:text-amber-200">orders@sterilemate.com</a>
            {" "}or call 281.456.3458 and we&apos;ll prepay-ship.
          </p>
          <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
            <Link to="/">Return Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-slate-100 py-12"
      style={{
        backgroundImage:
          "radial-gradient(1100px 600px at 0% 0%, rgba(14,165,233,0.25), transparent 60%)," +
          "radial-gradient(900px 600px at 100% 80%, rgba(37,99,235,0.20), transparent 55%)," +
          "linear-gradient(180deg, #0a1733 0%, #0b2148 50%, #0a1f44 100%)",
      }}
      data-testid="hospital-self-register-page"
    >
      <div className="max-w-3xl mx-auto px-4">
        <Link
          to="/hospital-login"
          className="inline-flex items-center gap-2 text-sm text-cyan-200/80 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Login
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 ring-1 ring-emerald-400/30 px-3 py-1.5 rounded-full mb-4">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-200">
              New Hospital Registration
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 leading-tight">
            Open your <span className="text-emerald-300">Sterile Mate account</span>
          </h1>
          <p className="text-slate-300 leading-relaxed max-w-xl mx-auto">
            Tell us about your facility and the primary contact. We&apos;ll review your application within one business day
            and email login details so you can start ordering, requesting quotes, and tracking shipments.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white/[0.06] backdrop-blur ring-1 ring-white/10 rounded-2xl p-6 md:p-8 space-y-8">
          {/* SECTION 1: Facility */}
          <section data-testid="section-facility">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-400/30 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-cyan-300" />
              </div>
              <h2 className="text-lg font-extrabold text-white">1 · Facility Profile</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-300">Facility Name *</Label>
                <Input
                  required
                  value={form.hospital_name}
                  onChange={update("hospital_name")}
                  placeholder="Memorial Hermann Texas Medical Center"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                  data-testid="reg-hospital-name"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Facility Type *</Label>
                <select
                  required
                  value={form.facility_type}
                  onChange={update("facility_type")}
                  className="mt-1 w-full h-10 rounded-md bg-white/10 border border-white/20 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  data-testid="reg-facility-type"
                >
                  {FACILITY_TYPES.map((t) => <option key={t} value={t} className="text-slate-900">{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-300">NPI <span className="text-slate-500">(optional)</span></Label>
                <Input
                  value={form.npi}
                  onChange={update("npi")}
                  placeholder="10-digit NPI"
                  inputMode="numeric"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                  data-testid="reg-npi"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Bed Count <span className="text-slate-500">(optional)</span></Label>
                <Input
                  value={form.bed_count}
                  onChange={update("bed_count")}
                  placeholder="e.g. 480"
                  inputMode="numeric"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                  data-testid="reg-bed-count"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-300 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" /> Street Address
                </Label>
                <Input
                  value={form.address}
                  onChange={update("address")}
                  placeholder="6411 Fannin St"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                  data-testid="reg-address"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">City</Label>
                <Input
                  value={form.city}
                  onChange={update("city")}
                  placeholder="Houston"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                  data-testid="reg-city"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-300">State</Label>
                  <Input
                    value={form.state}
                    maxLength={2}
                    onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="TX"
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                    data-testid="reg-state"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-300">ZIP</Label>
                  <Input
                    value={form.zip_code}
                    onChange={update("zip_code")}
                    placeholder="77030"
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-cyan-400"
                    data-testid="reg-zip"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 2: Primary user */}
          <section data-testid="section-user" className="pt-2 border-t border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center">
                <User className="w-4 h-4 text-amber-300" />
              </div>
              <h2 className="text-lg font-extrabold text-white">2 · Primary Contact</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 ml-auto">
                You can add additional users after approval
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-300">Full Name *</Label>
                <Input
                  required
                  value={form.contact_name}
                  onChange={update("contact_name")}
                  placeholder="Jane Doe"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-amber-400"
                  data-testid="reg-contact-name"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Title</Label>
                <Input
                  value={form.title}
                  onChange={update("title")}
                  placeholder="Director of Supply Chain"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-amber-400"
                  data-testid="reg-title"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-300">Department *</Label>
                <select
                  required
                  value={form.department}
                  onChange={update("department")}
                  className="mt-1 w-full h-10 rounded-md bg-white/10 border border-white/20 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="reg-department"
                >
                  {DEPARTMENTS.map((d) => <option key={d} value={d} className="text-slate-900">{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-300 flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Work Email *
                </Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="jane@yourhospital.org"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-amber-400"
                  data-testid="reg-email"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300 flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> Phone *
                </Label>
                <Input
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(281) 555-0123"
                  className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus-visible:ring-amber-400"
                  data-testid="reg-phone"
                />
              </div>
            </div>
          </section>

          {/* SECTION 3: Payment terms */}
          <section data-testid="section-terms" className="pt-2 border-t border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-emerald-300" />
              </div>
              <h2 className="text-lg font-extrabold text-white">3 · Payment Terms</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {["Net 30", "Prepay"].map((option) => (
                <label
                  key={option}
                  className={`cursor-pointer rounded-xl ring-2 transition-all p-4 ${
                    form.payment_terms_preference === option
                      ? "ring-emerald-400/70 bg-emerald-500/[0.08]"
                      : "ring-white/10 bg-white/[0.04] hover:ring-white/25"
                  }`}
                  data-testid={`reg-terms-${option === "Net 30" ? "net30" : "prepay"}`}
                >
                  <input
                    type="radio"
                    name="payment_terms_preference"
                    value={option}
                    checked={form.payment_terms_preference === option}
                    onChange={update("payment_terms_preference")}
                    className="sr-only"
                  />
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full ring-2 mt-0.5 flex items-center justify-center ${
                      form.payment_terms_preference === option ? "ring-emerald-400 bg-emerald-500" : "ring-white/30 bg-transparent"
                    }`}>
                      {form.payment_terms_preference === option && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </div>
                    <div>
                      <p className="font-bold text-white">{option}</p>
                      <p className="text-xs text-slate-300 mt-0.5 leading-snug">
                        {option === "Net 30"
                          ? "Invoice payable in 30 days. Credit review required (up to 24 hours)."
                          : "ACH / credit card / check on order placement. Ships immediately on payment receipt."}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Label className="text-xs text-slate-300">Anything else we should know? <span className="text-slate-500">(optional)</span></Label>
              <textarea
                value={form.reason}
                onChange={update("reason")}
                rows={3}
                placeholder="GPO affiliation, specific product needs, urgent timeline, etc."
                className="mt-1 w-full rounded-md bg-white/10 border border-white/20 text-white placeholder:text-slate-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y"
                data-testid="reg-notes"
              />
            </div>
          </section>

          <div className="pt-4 border-t border-white/10 flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/hospital-login")}
              className="border-white/20 bg-white/5 text-slate-200 hover:bg-white/10"
              data-testid="reg-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-12"
              data-testid="reg-submit"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                : <>Submit Registration <ArrowRight className="w-4 h-4 ml-2" /></>
              }
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 text-center">
            By submitting you acknowledge that pricing and credit terms are confirmed at PO. Manufacturer-direct, non-GPO.
          </p>
        </form>
      </div>
    </div>
  );
}
